import * as React from 'react';
import Box from '@mui/material/Box';

export interface StatusRibbonProps {
  isActive?: boolean | null;
}

/**
 * Header top padding (theme spacing units) a card needs above its title row so the hanging tag
 * clears it: the tag is ~24px tall at the largest font preset, plus a little air.
 */
export const STATUS_RIBBON_CLEARANCE = 3.5;

/**
 * Active/Inactive as a band along the top edge of a detail card: a full-width 4px strip in the
 * status colour with a small tag hanging from its middle. It replaces the status Chip that used to
 * sit inline with the title, which read as one more word in the heading.
 *
 * The strip is a block in normal flow, not an absolutely positioned overlay, so it takes the
 * card's rounded corners from the card's own overflow clipping and never collides with the header
 * content. Only the tag is positioned (below the strip), which is why consumers pad the header by
 * `STATUS_RIBBON_CLEARANCE`. Never make it a direct child of a flex row: a 4px box collapses there.
 */
export default function StatusRibbon({ isActive }: StatusRibbonProps) {
  const tone = isActive ? 'success' : 'error';
  const text = isActive ? 'Active' : 'Inactive';
  return (
    <Box
      role='status'
      aria-label={`${text} status`}
      data-status-ribbon={tone}
      sx={{ position: 'relative', height: 4, bgcolor: `${tone}.main`, flex: '0 0 auto' }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          bgcolor: `${tone}.main`,
          color: `${tone}.contrastText`,
          px: 1.25,
          py: '2px',
          lineHeight: 1.4,
          borderRadius: '0 0 6px 6px',
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Box>
    </Box>
  );
}
